import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import VoiceRecorder from './VoiceRecorder';
import { Sparkles, Eye, EyeOff, Loader2, Undo2, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

/**
 * DictationInput - Textarea with speech-to-text, AI cleanup, and smart prompts
 *
 * Props:
 * - value: current text value
 * - onChange: callback with (cleanedText, originalText)
 * - placeholder: textarea placeholder
 * - rows: textarea rows
 * - disabled: disable input
 * - className: additional classes
 * - showCleanup: whether to show AI cleanup button (default true)
 * - promptContext: object { taskName, taskDescription, category, context }
 *     When provided, shows contextual smart prompts to guide the user
 * - data-testid: test ID
 */
export default function DictationInput({
  value = '',
  onChange,
  placeholder = 'Type or speak your response...',
  rows = 4,
  disabled = false,
  className = '',
  showCleanup = true,
  promptContext = null,
  'data-testid': testId = 'dictation-input',
}) {
  const { axiosAuth } = useAuth();
  const [originalText, setOriginalText] = useState('');
  const [showOriginal, setShowOriginal] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [hasBeenCleaned, setHasBeenCleaned] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);

  const loadPrompts = useCallback(async (openOnLoad = false) => {
    if (!promptContext) return;
    setPromptsLoading(true);
    try {
      const api = axiosAuth();
      const params = {};
      if (promptContext.taskName) params.task_name = promptContext.taskName;
      if (promptContext.taskDescription) params.task_description = promptContext.taskDescription;
      if (promptContext.category) params.category = promptContext.category;
      if (promptContext.context) params.context = promptContext.context;
      const res = await api.get('/ai/smart-prompts', { params });
      if (res.data?.prompts) {
        setPrompts(res.data.prompts);
        if (openOnLoad) setPromptsOpen(true);
      }
    } catch {
      // silently fail — prompts are supplementary
    } finally {
      setPromptsLoading(false);
    }
  }, [promptContext, axiosAuth]);

  // Load smart prompts when context is provided
  useEffect(() => {
    if (!promptContext) return;
    loadPrompts(false);
  }, [promptContext, loadPrompts]);

  useEffect(() => {
    if (promptsOpen) {
      loadPrompts(false);
    }
  }, [promptsOpen, loadPrompts]);

  const handleTextChange = (e) => {
    const text = e.target.value;
    if (onChange) onChange(text, originalText || text);
    if (hasBeenCleaned) {
      setHasBeenCleaned(false);
      setOriginalText('');
    }
  };

  const handleTranscription = useCallback((text) => {
    const current = value || '';
    const newText = current ? `${current} ${text}` : text;
    setOriginalText(newText);
    if (onChange) onChange(newText, newText);
    toast.success('Speech transcribed');
  }, [value, onChange]);

  const handleCleanup = async () => {
    if (!value?.trim()) return;
    setCleaning(true);
    try {
      const api = axiosAuth();
      if (!originalText) setOriginalText(value);
      const params = { text: value };
      if (promptContext?.context) params.context = promptContext.context;
      if (promptContext?.taskName) params.task_name = promptContext.taskName;
      if (promptContext?.taskDescription) params.task_description = promptContext.taskDescription;
      if (promptContext?.category) params.category = promptContext.category;
      const res = await api.post('/ai/cleanup-text', null, { params });
      if (res.data.cleaned) {
        if (onChange) onChange(res.data.cleaned, originalText || value, res.data.analysis);
        setHasBeenCleaned(true);
        toast.success('Text refined by AI');
      }
    } catch {
      toast.error('AI refinement failed');
    } finally {
      setCleaning(false);
    }
  };

  const revertToOriginal = () => {
    if (originalText) {
      if (onChange) onChange(originalText, originalText);
      setHasBeenCleaned(false);
      toast.info('Reverted to original text');
    }
  };

  const insertPrompt = (prompt) => {
    const current = value || '';
    const newText = current ? `${current}\n\n${prompt} ` : `${prompt} `;
    if (onChange) onChange(newText, newText);
    setPromptsOpen(false);
  };

  return (
    <div className={`space-y-2 ${className}`} data-testid={testId}>
      {/* Smart Prompts */}
      {prompts.length > 0 && (
        <div className="rounded-xl border border-teal-200 bg-teal-50/50 overflow-hidden" data-testid={`${testId}-prompts`}>
          <div className="flex items-center justify-between px-3 py-2 text-xs font-medium text-teal-700">
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-teal-800"
              onClick={() => setPromptsOpen(!promptsOpen)}
              data-testid={`${testId}-prompts-toggle`}
            >
              <span className="flex items-center gap-1.5">
                <Lightbulb size={13} />
                Need help? Try a speaking prompt
              </span>
              {promptsOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            <button
              type="button"
              className="text-[11px] text-teal-600 hover:text-teal-800"
              onClick={() => loadPrompts(true)}
              data-testid={`${testId}-prompts-refresh`}
            >
              {promptsLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          {promptsOpen && (
            <div className="px-3 pb-2.5 space-y-1.5">
              {prompts.map((p, i) => (
                <button
                  key={`prompt-${i}`}
                  type="button"
                  className="w-full text-left p-2 rounded-lg bg-white hover:bg-teal-100 border border-teal-100 text-xs text-slate-700 transition-colors"
                  onClick={() => insertPrompt(p)}
                  data-testid={`${testId}-prompt-${i}`}
                >
                  "{p}"
                </button>
              ))}
              <p className="text-[10px] text-teal-600/60 pt-0.5 text-center">Tap a prompt to use it as a starter, then add your own words</p>
            </div>
          )}
        </div>
      )}

      {/* Text area with mic button */}
      <div className="relative">
        <Textarea
          value={showOriginal && originalText ? originalText : (value || '')}
          onChange={handleTextChange}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled || showOriginal}
          className={`pr-12 ${showOriginal ? 'bg-amber-50 border-amber-200' : ''}`}
          data-testid={`${testId}-textarea`}
        />
        <div className="absolute top-2 right-2">
          <VoiceRecorder onTranscription={handleTranscription} size="icon" />
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {showCleanup && value?.trim() && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCleanup}
            disabled={cleaning || showOriginal}
            className="rounded-full gap-1.5 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
            data-testid={`${testId}-cleanup-btn`}
          >
            {cleaning ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {cleaning ? 'Refining...' : 'AI Refine'}
          </Button>
        )}

        {hasBeenCleaned && originalText && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowOriginal(!showOriginal)}
              className="rounded-full gap-1.5 text-xs"
              data-testid={`${testId}-toggle-original`}
            >
              {showOriginal ? <EyeOff size={12} /> : <Eye size={12} />}
              {showOriginal ? 'Show Refined' : 'View Original'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={revertToOriginal}
              className="rounded-full gap-1.5 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              data-testid={`${testId}-revert-btn`}
            >
              <Undo2 size={12} /> Revert
            </Button>
            <Badge className="bg-teal-50 text-teal-700 border-0 text-[10px]">
              Refined for Authorities
            </Badge>
          </>
        )}

        {showOriginal && originalText && (
          <Badge className="bg-amber-50 text-amber-700 border-0 text-[10px]">
            Viewing Original Dictation
          </Badge>
        )}
      </div>
    </div>
  );
}
